import React, { useMemo } from 'react';

import { Select, type SelectProps, Spin } from 'antd';
import { useDebouncedState } from 'hooks/useDebouncedState';

export type Entity<IdType = number> = {
  id: IdType;
  name: string;
} | null;

export interface AsyncSelectProps<IdType = number> extends SelectProps {
  mode?: never; // Компонент не поддерживает множественный выбор
  value?: Entity<IdType>;
  onChange?: (entity: Entity<IdType>) => void;
}

/**
 * Фабричная функция для создания асинхронного выпадающего списка (Select) с поддержкой поиска по сущностям (Entity).
 *
 * ⚠️ **Важно:** вызывать `createAsyncSelect` необходимо **вне React-компонента** —
 * это фабрика, создающая новый компонент с хуком внутри (`useFetchQuery`).
 *
 * —
 * ### Назначение
 * `createAsyncSelect` используется для построения типового асинхронного Select-компонента,
 * который подгружает список сущностей (`Entity`) по мере ввода текста.
 *
 * —
 * ### generic-тип
 * Можно передать generic-тип поля id у Entity для правильной типизации. Возможные опции: string | number
 *
 * —
 * ### Пример использования
 *
 *     ```tsx
 *     const UserSelect = createAsyncSelect(usersApi.useFetchUsersQuery);
 *
 *     export const Form = () => {
 *       const [user, setUser] = useState<Entity>(null);
 *
 *       return (
 *         <UserSelect
 *           value={user}
 *           onChange={setUser}
 *           placeholder={"Выберите пользователя"}
 *         />
 *       );
 *     };
 *     ```
 *
 * @template useFetchQuery - RTK Query-хук (или любой другой хук данных), принимающий `search` и возвращающий `{ data, isFetching }`
 * @param useFetchQuery - Хук для получения списка сущностей по параметру `search`
 * @param staticParams - Статические параметры запроса (фильтры и т.п.), которые будут передаваться дополнительно
 * @returns Компонент `<AsyncSelect />`, который можно использовать внутри React-компонента
 */
export const createAsyncSelect = <IdType extends string | number = number>(
  useFetchQuery: (params: { search?: string }) => {
    data?: { data: Entity<IdType>[] };
    isFetching: boolean;
  },
  staticParams: Record<string, unknown> = {}
) => {
  return ({ value, onChange, ...props }: AsyncSelectProps<IdType>) => {
    const [search, debouncedSearch, setSearch] = useDebouncedState('');

    const { data, isFetching } = useFetchQuery({
      search: debouncedSearch || undefined,
      ...staticParams,
    });

    const options = useMemo(() => {
      const normalizedSearch = search.trim().toLowerCase();

      const fetchedOptions =
        data?.data?.map((entity) => ({
          value: entity?.id,
          label: entity?.name,
        })) ?? [];

      const shouldShowSelectedOption =
        value && (!normalizedSearch || value.name.toLowerCase().includes(normalizedSearch));

      const selectedOption = shouldShowSelectedOption
        ? [
            {
              value: value.id,
              label: value.name,
            },
          ]
        : [];

      return [...selectedOption, ...fetchedOptions.filter((opt) => opt.value !== value?.id)];
    }, [data, value, search]);

    return (
      <Select
        showSearch
        allowClear
        filterOption={false}
        value={value?.id}
        options={options}
        searchValue={search}
        notFoundContent={isFetching ? <Spin size="small" /> : null}
        onSearch={setSearch}
        onClear={() => {
          setSearch('');
          onChange?.(null);
        }}
        onChange={(id, option) => {
          setSearch('');

          if (!id) {
            onChange?.(null);
            return;
          }

          const opt = option as { value: IdType; label: string };

          onChange?.({
            id: opt.value,
            name: opt.label,
          });
        }}
        {...props}
      />
    );
  };
};
